import {
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonSpinner,
  IonToggle,
  useIonViewWillEnter,
} from "@ionic/react";
import { useContext, useRef, useState } from "react";
import { TranslationsContext } from "../../util/Translations";
import {
  MESSAGING_ENABLED,
  MESSAGING_SECTION,
  MessagingSettings,
  NOTIFY_TYPES,
  ReadOnlyNote,
  SettingsSubPage,
  readMessagingSettings,
  saveEamSetting,
  useEamSettingsReadOnly,
  useSaveErrorToast,
} from "./settingsShared";

/** "Off" or "On · n of 4" (one per NOTIFY_TYPES entry), used for the summary on the Settings page. */
export const getNotificationSummary = (
  settings: MessagingSettings,
  translations: Record<string, string>
) => {
  if (!settings.enabled) return translations["lbl_off"] || "Off";
  const onCount = NOTIFY_TYPES.filter((t) => settings.types[t.parameter]).length;
  return `${translations["lbl_on"] || "On"} · ${onCount} ${
    translations["lbl_mob_of"] || "of"
  } ${NOTIFY_TYPES.length}`;
};

const NotificationSettings: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const readOnly = useEamSettingsReadOnly();
  const showSaveError = useSaveErrorToast();
  const [settings, setSettings] = useState<MessagingSettings>();
  const [loadFailed, setLoadFailed] = useState(false);
  // last values confirmed by EAM; ionChange also fires for programmatic changes
  const saved = useRef<MessagingSettings>();

  useIonViewWillEnter(() => {
    readMessagingSettings()
      .then((value) => {
        saved.current = value;
        setSettings(value);
        setLoadFailed(false);
      })
      .catch(() => setLoadFailed(true));
  });

  const setAllowNotifications = async (enabled: boolean) => {
    if (!saved.current || enabled === saved.current.enabled) return;
    const previous = saved.current;
    setSettings({ ...previous, enabled });
    const ok = await saveEamSetting(
      MESSAGING_SECTION,
      MESSAGING_ENABLED,
      enabled ? "1" : "0"
    );
    if (!ok) {
      setSettings(previous);
      showSaveError();
      return;
    }
    saved.current = { ...previous, enabled };
  };

  const setTypeEnabled = async (parameter: string, enabled: boolean) => {
    if (!saved.current || enabled === saved.current.types[parameter]) return;
    const previous = saved.current;
    const next = { ...previous, types: { ...previous.types, [parameter]: enabled } };
    setSettings(next);
    const ok = await saveEamSetting(MESSAGING_SECTION, parameter, enabled ? "1" : "0");
    if (!ok) {
      setSettings(previous);
      showSaveError();
      return;
    }
    saved.current = next;
  };

  return (
    <SettingsSubPage
      title={translations["lbl_mob_settings_notifications"] || "Notifications"}
    >
      {readOnly && <ReadOnlyNote />}
      {!settings ? (
        loadFailed ? (
          !readOnly && (
            <IonNote className="settings-readonly-note">
              {translations["lbl_mob_notifications_load_failed"] ||
                "Unable to load notification settings."}
            </IonNote>
          )
        ) : (
          <div className="ion-text-center ion-padding">
            <IonSpinner />
          </div>
        )
      ) : (
        <>
          <IonList inset className="settings-list" style={{ marginTop: 16 }}>
            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">
                <h2>
                  {translations["lbl_mob_allow_notifications"] ||
                    "Allow notifications"}
                </h2>
                <p>
                  {translations["lbl_mob_allow_notifications_help"] ||
                    "Also turns EAM messaging on or off for you on the web."}
                </p>
              </IonLabel>
              <IonToggle
                slot="end"
                checked={settings.enabled}
                disabled={readOnly}
                onIonChange={(e) => setAllowNotifications(e.detail.checked)}
              />
            </IonItem>
          </IonList>
          <div className="settings-section-title">
            {translations["lbl_mob_notify_me_about"] || "Notify me about"}
          </div>
          <IonList inset className="settings-list">
            {NOTIFY_TYPES.map((type) => (
              <IonItem key={type.parameter}>
                <IonLabel>{translations[type.labelKey] || type.label}</IonLabel>
                <IonToggle
                  slot="end"
                  checked={settings.types[type.parameter]}
                  disabled={readOnly || !settings.enabled}
                  onIonChange={(e) =>
                    setTypeEnabled(type.parameter, e.detail.checked)
                  }
                />
              </IonItem>
            ))}
          </IonList>
        </>
      )}
    </SettingsSubPage>
  );
};

export default NotificationSettings;
